/**
 * ItemConfigModal — configure an item then add it to the cart.
 * Handles size/option variations, Square modifier lists, and the manager-defined
 * dynamic option groups (dosa type, add-ons like ghee/onion/podi) rendered as
 * single- or multi-select dropdowns. Prices update live.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useOrdering } from './OrderingContext';
import { fmt$ } from './orderApi';
import { useOverlay } from './overlayHooks';

const cents$ = (c) => `$${((c || 0) / 100).toFixed(2)}`;

/** Manager-defined option group rendered as a single/multi-select dropdown. */
function OptionDropdown({ group, selected, onToggle, showErr }) {
  const [open, setOpen] = useState(false);
  const sel = selected || new Set();
  const chosen = (group.options || []).filter(o => sel.has(o.id));
  const isMulti = group.type === 'multi';
  const required = isMulti ? (group.min || 0) > 0 : group.required === true;
  const unmet = showErr && (isMulti ? (group.min || 0) > sel.size : (required && sel.size < 1));
  const rule = isMulti
    ? ((group.min || 0) > 0 ? `Choose ${group.min}–${group.max || group.options.length}` : `Optional · up to ${group.max || group.options.length}`)
    : (required ? 'Required' : 'Optional');
  const summary = chosen.length
    ? chosen.map(o => o.name + (o.priceCents > 0 ? ` (+${cents$(o.priceCents)})` : '')).join(', ')
    : 'Select…';

  return (
    <div className={`osc-group${unmet ? ' osc-group--err' : ''}`}>
      <div className="osc-group__head">
        <span>{group.name}</span>
        <span className={`osc-req${required ? '' : ' osc-req--opt'}`}>{rule}</span>
      </div>
      <button type="button" className={`osc-dd__toggle${open ? ' osc-dd__toggle--open' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className={chosen.length ? '' : 'osc-dd__ph'}>{summary}</span>
        <span className="osc-dd__chev" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="osc-dd__panel">
          {group.options.map(o => {
            const on = sel.has(o.id);
            return (
              <button
                key={o.id} type="button"
                className={`osc-dd__opt${on ? ' osc-dd__opt--on' : ''}`}
                onClick={() => { onToggle(group, o.id); if (!isMulti) setOpen(false); }}>
                <span className={`osc-mod__box${!isMulti ? ' osc-mod__box--radio' : ''}`}>{on ? '✓' : ''}</span>
                <span className="osc-dd__name">{o.name}</span>
                {o.priceCents > 0 && <span className="osc-mod__price">+{cents$(o.priceCents)}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ItemConfigModal() {
  const { activeItem, closeItem, addConfigured, notify } = useOrdering();

  const variations = useMemo(() => activeItem?.variations || [], [activeItem]);
  const firstInStock = useMemo(
    () => variations.find(v => !v.soldOut) || variations[0] || null,
    [variations]
  );

  const [variationId, setVariationId] = useState(firstInStock?.id || null);
  const [selected, setSelected] = useState({}); // listId → Set(modifierId)
  const [optSel, setOptSel] = useState({});      // groupId → Set(optionId)  (custom groups)
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [showErr, setShowErr] = useState(false);

  useEffect(() => {
    setVariationId(firstInStock?.id || null);
    setSelected({});
    setOptSel({});
    setQty(1);
    setNote('');
    setShowErr(false);
  }, [activeItem, firstInStock]);

  useOverlay(!!activeItem, closeItem);

  if (!activeItem) return null;

  const lists = activeItem.modifierLists || [];
  const groups = activeItem.optionGroups || [];
  const variation = variations.find(v => v.id === variationId) || firstInStock;

  const toggleMod = (list, modId) => {
    setSelected(prev => {
      const cur = new Set(prev[list.id] || []);
      if (list.selectionType === 'SINGLE' || list.max === 1) {
        return { ...prev, [list.id]: new Set([modId]) };
      }
      if (cur.has(modId)) cur.delete(modId);
      else {
        if (list.max && cur.size >= list.max) return prev; // respect max
        cur.add(modId);
      }
      return { ...prev, [list.id]: cur };
    });
  };

  // Custom option groups (dosa type, add-ons). Single replaces; multi toggles up to max.
  const toggleOpt = (group, optId) => {
    setOptSel(prev => {
      const cur = new Set(prev[group.id] || []);
      if (group.type !== 'multi') return { ...prev, [group.id]: new Set([optId]) };
      if (cur.has(optId)) cur.delete(optId);
      else {
        if (group.max && cur.size >= group.max) return prev; // respect max
        cur.add(optId);
      }
      return { ...prev, [group.id]: cur };
    });
  };

  const chosenModifiers = lists.flatMap(list =>
    (list.modifiers || []).filter(m => (selected[list.id] || new Set()).has(m.id))
  );
  const chosenOptions = groups.flatMap(g =>
    (g.options || []).filter(o => (optSel[g.id] || new Set()).has(o.id))
  );

  const baseCents = variation?.priceCents || activeItem.basePriceCents || 0;
  const modCents = chosenModifiers.reduce((s, m) => s + (m.priceCents || 0), 0);
  const optCents = chosenOptions.reduce((s, o) => s + (o.priceCents || 0), 0);
  const unitCents = baseCents + modCents + optCents;

  const unmetLists = lists.filter(l => (l.min || 0) > (selected[l.id]?.size || 0));
  const unmetGroups = groups.filter(g => {
    const n = optSel[g.id]?.size || 0;
    return g.type === 'multi' ? (g.min || 0) > n : (g.required === true && n < 1);
  });
  const valid = !!variation && !variation.soldOut && unmetLists.length === 0 && unmetGroups.length === 0;

  const add = () => {
    if (!valid) { setShowErr(true); return; }
    addConfigured({
      catalogObjectId: activeItem.id,
      variationId: variation.id,
      variationName: variation.name,
      name: activeItem.name,
      squareName: activeItem.name,
      basePrice: baseCents / 100,
      modifiers: [
        ...chosenModifiers.map(m => ({ id: m.id, name: m.name, priceCents: m.priceCents || 0 })),
        // Custom (manager-defined) selections — flagged so the order backend
        // routes the line to ad-hoc pricing (they aren't Square catalog mods).
        ...chosenOptions.map(o => ({ id: `custom:${o.id}`, name: o.name, priceCents: o.priceCents || 0, custom: true })),
      ],
      note: note.trim(),
    }, qty);
    closeItem();
    notify?.(`Added ${qty > 1 ? `${qty} × ` : ''}${activeItem.name}`);
  };

  return (
    <div className="ord-overlay" onClick={closeItem}>
      <div className="osc-sheet" onClick={e => e.stopPropagation()} role="dialog" aria-label={`Add ${activeItem.name}`}>
        {activeItem.imageUrl && (
          <div className="osc-sheet__img" style={{ backgroundImage: `url(${activeItem.imageUrl})` }} />
        )}
        <button className="osc-sheet__x" onClick={closeItem} aria-label="Close">✕</button>

        <div className="osc-sheet__body">
          <h3 className="osc-sheet__name">{activeItem.emoji ? `${activeItem.emoji} ` : ''}{activeItem.name}</h3>
          <div className="osc-sheet__price">{variations.length > 1 ? `from ${cents$(Math.min(...variations.map(v => v.priceCents)))}` : cents$(baseCents)}</div>
          {activeItem.description && <p className="osc-sheet__desc">{activeItem.description}</p>}

          {/* Variations */}
          {variations.length > 1 && (
            <div className="osc-group">
              <div className="osc-group__head"><span>Choose size</span><span className="osc-req">Required</span></div>
              <div className="osc-opts">
                {variations.map(v => (
                  <button
                    key={v.id}
                    className={`osc-opt${variationId === v.id ? ' osc-opt--on' : ''}${v.soldOut ? ' osc-opt--off' : ''}`}
                    disabled={v.soldOut}
                    onClick={() => !v.soldOut && setVariationId(v.id)}>
                    <span>{v.name}{v.soldOut ? ' · sold out' : ''}</span>
                    <span className="osc-opt__price">{cents$(v.priceCents)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Modifier lists */}
          {lists.map(list => {
            const sel = selected[list.id] || new Set();
            const unmet = showErr && (list.min || 0) > sel.size;
            const ruleLabel = list.selectionType === 'SINGLE' || list.max === 1
              ? 'Choose 1'
              : list.min > 0 ? `Choose ${list.min}–${list.max || (list.modifiers || []).length}` : `Optional · up to ${list.max || (list.modifiers || []).length}`;
            return (
              <div className={`osc-group${unmet ? ' osc-group--err' : ''}`} key={list.id}>
                <div className="osc-group__head">
                  <span>{list.name}</span>
                  <span className={`osc-req${list.min > 0 ? '' : ' osc-req--opt'}`}>{ruleLabel}</span>
                </div>
                <div className="osc-mods">
                  {(list.modifiers || []).map(m => {
                    const on = sel.has(m.id);
                    const single = list.selectionType === 'SINGLE' || list.max === 1;
                    return (
                      <button key={m.id} className={`osc-mod${on ? ' osc-mod--on' : ''}`} onClick={() => toggleMod(list, m.id)}>
                        <span className={`osc-mod__box${single ? ' osc-mod__box--radio' : ''}`}>{on ? '✓' : ''}</span>
                        <span className="osc-mod__name">{m.name}</span>
                        {m.priceCents > 0 && <span className="osc-mod__price">+{cents$(m.priceCents)}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Manager-defined option groups (dosa type, add-ons) — dropdowns */}
          {groups.map(g => (
            <OptionDropdown
              key={g.id}
              group={g}
              selected={optSel[g.id]}
              onToggle={toggleOpt}
              showErr={showErr}
            />
          ))}

          {/* Special instructions */}
          <div className="osc-group">
            <div className="osc-group__head"><span>Special instructions</span><span className="osc-req osc-req--opt">Optional</span></div>
            <textarea className="osc-note" value={note} maxLength={200} rows={2}
              onChange={e => setNote(e.target.value)} placeholder="e.g. no onion, allergy, extra crispy" />
          </div>

          {showErr && !valid && (
            <div className="osc-err">Please complete the required choices above.</div>
          )}
        </div>

        {/* Footer: qty + add */}
        <div className="osc-sheet__foot">
          <div className="ord-qty ord-qty--lg">
            <button onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="Decrease">−</button>
            <span>{qty}</span>
            <button onClick={() => setQty(q => Math.min(99, q + 1))} aria-label="Increase">+</button>
          </div>
          <button className="ord-btn ord-btn--primary ord-btn--block osc-add" onClick={add} disabled={!!variation?.soldOut}>
            <span>Add {qty > 1 ? `${qty} ` : ''}to order</span>
            <span>{fmt$((unitCents / 100) * qty)}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
